import {
  useAllRawTransactionsLiteQuery,
  useBalanceAnchorsQuery,
} from '@transactions/graphql';
import Money from 'common/Money';
import {DateTime} from 'luxon';
import * as React from 'react';
import {toDisplayBalance} from 'src/account/balanceDisplay';
import {
  ErrorText,
  LoadMoreButton,
  LoadMoreRow,
  TransactionList,
  TransactionRow,
} from 'src/AccountLedgerView/TransactionSelectorComponents';
import {Button} from 'src/Button';
import {useMoneyFormatter} from 'src/hooks/useUnits';
import Empty from 'src/ListView/Empty';
import {Modal} from 'src/Modal';
import {ReconcileMathSummary} from 'src/reconcile/ReconcileMathSummary';
import {
  inReconcileWindow,
  useReconcileSession,
} from 'src/reconcile/useReconcileSession';
import type {AllRawTransactionsLiteQueryVariables} from '@transactions/graphql';
import type {SystemProps} from 'src/Modals';

export type ReconcileAnchorModalUserProps = {
  anchorId: string;
  accountId: string;
  /** ISO date of the anchor (used as endDate filter) */
  anchorDate: string;
  /** Anchor balance in cents */
  anchorBalance: number;
  currency: string;
  /**
   * Credit card balances are stored negative (liability convention). When true,
   * displayed amounts are sign-flipped so the summary reads as "amount owed"
   * (prev owed + new charges = new owed). The reconciliation math itself still
   * runs on the raw signed values.
   */
  isCreditCard?: boolean;
  onSuccess?: () => void;
};

/**
 * Rows per fetch. Reconciliation must load the whole period before it can
 * seed the selection, so fewer round trips beat smaller payloads — and the
 * lite query's rows are small (no account/benefit sub-objects).
 */
const PAGE_SIZE = 500;

/**
 * A cursor-paginated raw-transaction list. Pages are newest-first; each
 * loadNextPage() call appends the next-older page. The cursor guard keeps
 * React strict-mode double-effects (and a manual click racing an auto-load
 * effect) from appending the same page twice.
 *
 * Uses the lite projection (no account/benefit) — the full BasicRawTransaction
 * fragment triggers per-row account sub-resolvers on the server, which made
 * loading this modal slow.
 */
const usePagedRawTransactions = ({
  variables,
  skip,
}: {
  variables: AllRawTransactionsLiteQueryVariables;
  skip: boolean;
}) => {
  const {data, loading, fetchMore} = useAllRawTransactionsLiteQuery({
    variables,
    skip,
    // Makes `loading` reflect fetchMore, so load-more buttons can disable
    // themselves while a page is in flight.
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'cache-and-network',
  });
  const page = data?.allRawTransactions;
  const fetchedCursors = React.useRef(new Set<string>());
  const loadNextPage = React.useCallback(() => {
    if (!page?.hasMore || !page.endCursor) return;
    if (fetchedCursors.current.has(page.endCursor)) return;
    const cursor = page.endCursor;
    fetchedCursors.current.add(cursor);
    void fetchMore({
      variables: {beforeCursor: cursor},
      updateQuery: (prev, {fetchMoreResult}) => ({
        ...prev,
        allRawTransactions: {
          ...fetchMoreResult.allRawTransactions,
          items: [
            ...prev.allRawTransactions.items,
            ...fetchMoreResult.allRawTransactions.items,
          ],
        },
      }),
    }).catch(() => {
      // A transient failure must not pin the guard forever — forget the
      // cursor so the auto-paging loop and Load buttons can retry it.
      fetchedCursors.current.delete(cursor);
    });
  }, [page, fetchMore]);

  return {
    items: page?.items ?? [],
    hasMore: page?.hasMore ?? false,
    loading,
    loadNextPage,
  };
};

export const ReconcileAnchorModal = (
  props: ReconcileAnchorModalUserProps & SystemProps,
) => {
  // Display sign: credit cards are shown as "amount owed".
  const isCreditCard = props.isCreditCard ?? false;
  const formatMoney = useMoneyFormatter();
  const fmt = (cents: number, currency: string = props.currency) =>
    formatMoney(
      Money.fromServer({amount: toDisplayBalance(cents, isCreditCard), currency}),
    );

  // Fetch the reconciliation baseline from the server, mirroring the reconcile
  // mutation's derivation so the modal's math always matches the server's
  // validation: the nearest prior anchor. Daily Plaid readings live in their
  // own collection and never appear here, which is what makes the period span
  // "since the last attested balance" rather than one sync day.
  const {data: anyPrevData, loading: prevLoading} = useBalanceAnchorsQuery({
    variables: {
      accountId: props.accountId,
      before: props.anchorDate,
      limit: 1,
    },
    fetchPolicy: 'cache-and-network',
  });
  const baseline = prevLoading ? null : (anyPrevData?.balanceAnchors[0] ?? null);
  // The server requires a prior anchor to baseline the reconciliation, so
  // the account's first anchor can't be reconciled directly — the user must
  // record a starting balance (e.g. the opening balance) first.
  const needsStartingBalance = !prevLoading && baseline == null;

  // The server doesn't constrain transaction dates (late-posting transactions
  // may legitimately belong to a neighboring period), so the main fetch is
  // only bounded by the anchor date. Pages are newest-first: the first
  // pages cover the period window (baseline.date, anchor.date], and older
  // history — including already-reconciled rows, shown disabled for context —
  // arrives via "Load earlier". The first page runs in parallel with the
  // baseline query (it doesn't depend on it); only the auto-paging loop below
  // waits for the baseline, since without one the window is unbounded.
  const {
    items: mainTxns,
    hasMore,
    loading,
    loadNextPage,
  } = usePagedRawTransactions({
    variables: {
      accountId: props.accountId,
      endDate: props.anchorDate,
      // Reconciliation is about ALL account activity in the period, including
      // transactions already categorized (remainingAmount === 0). Without this
      // the server's default needs-categorization filter would hide them and
      // the balance could never reconcile.
      includeProcessed: true,
      limit: PAGE_SIZE,
    },
    skip: needsStartingBalance,
  });

  // Transactions dated AFTER the anchor can also belong to this statement
  // (banks sometimes post with a later date than the balance reflects), and
  // the reconcile mutation accepts them. They're loaded on demand via "Load
  // later transactions" and fetched in full once requested — this set is
  // bounded by (anchor.date, today].
  const [includeLater, setIncludeLater] = React.useState(false);
  const dayAfterAnchor = DateTime.fromISO(props.anchorDate)
    .plus({days: 1})
    .toISODate();
  const {
    items: laterTxns,
    hasMore: laterHasMore,
    loading: laterLoading,
    loadNextPage: loadNextLaterPage,
  } = usePagedRawTransactions({
    variables: {
      accountId: props.accountId,
      startDate: dayAfterAnchor,
      includeProcessed: true,
      limit: PAGE_SIZE,
    },
    skip: prevLoading || needsStartingBalance || !includeLater,
  });
  React.useEffect(() => {
    if (!includeLater || laterLoading || !laterHasMore) return;
    loadNextLaterPage();
  }, [includeLater, laterLoading, laterHasMore, loadNextLaterPage]);

  const inWindow = (t: {date: string}) =>
    inReconcileWindow(
      {baselineDate: baseline?.date ?? null, anchorDate: props.anchorDate},
      t.date,
    );

  // Reconciliation needs the FULL window — the mutation only accepts a
  // transaction set that makes the period balance, so a truncated window would
  // make some periods impossible to reconcile. Pages are sorted newest-first,
  // so the window is covered once the oldest loaded row is at or before the
  // baseline date (or there is nothing left to load).
  const oldestLoaded =
    mainTxns.length > 0 ? mainTxns[mainTxns.length - 1].date : null;
  const windowCovered =
    !hasMore ||
    (baseline != null && oldestLoaded != null && oldestLoaded <= baseline.date);

  // Auto-fetch until the window is covered; beyond that, older history loads
  // on demand via the button. Waits for the baseline — without it the window
  // is unbounded and this loop would page through the account's entire
  // history (the first page has already been fetched in parallel by then).
  React.useEffect(() => {
    if (prevLoading || needsStartingBalance || loading || windowCovered) return;
    loadNextPage();
  }, [prevLoading, needsStartingBalance, loading, windowCovered, loadNextPage]);

  // Later transactions render above the main list, keeping everything
  // newest-first.
  const allTxns = [...laterTxns, ...mainTxns];

  const [selected, setSelected] = React.useState<Set<string>>(() => new Set());

  const session = useReconcileSession({
    accountId: props.accountId,
    anchorId: props.anchorId,
    anchorDate: props.anchorDate,
    anchorBalance: props.anchorBalance,
    baselineDate: baseline?.date ?? null,
    baselineBalance: baseline?.balance ?? 0,
    baselineResolved: !prevLoading,
    transactions: allTxns,
    windowCovered,
    loadingTransactions: loading,
    selectedIds: [...selected],
    // Only in-window transactions are auto-selected — earlier ones (loaded on
    // demand) are always an explicit opt-in.
    onSeedSelection: (txns) => setSelected(new Set(txns.map((t) => t._id))),
  });

  const toggle = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const succeeded = await session.submit();
    if (succeeded) {
      props.onSuccess?.();
      props.onRequestClose();
    }
  };

  const footer = (
    <>
      <Button
        variant="outline"
        disabled={session.submitting}
        onClick={props.onRequestClose}
      >
        Cancel
      </Button>
      <Button
        variant="primary"
        type="submit"
        form="reconcile-form"
        disabled={!session.canSubmit}
      >
        Reconcile
      </Button>
    </>
  );

  return (
    <Modal
      onRequestClose={props.onRequestClose}
      header={`Reconcile balance — ${props.anchorDate}`}
      footer={footer}
    >
      <form
        id="reconcile-form"
        onSubmit={(e) => void handleSubmit(e)}
        className="flex flex-col gap-4"
      >
        {needsStartingBalance && (
          <p className="border-border bg-muted m-0 rounded-lg border p-3 text-sm">
            This is the account's earliest recorded balance, so there is no
            previous balance to reconcile against. Record a starting balance
            (e.g. the account's opening balance) dated before{' '}
            {props.anchorDate}, then reconcile this one against it.
          </p>
        )}

        <ReconcileMathSummary
          baselineDate={baseline?.date ?? null}
          baselineBalance={baseline?.balance ?? 0}
          recordedDate={props.anchorDate}
          needsStartingBalance={needsStartingBalance}
          selectedSum={session.selectedSum}
          recordedBalance={props.anchorBalance}
          isBalanced={session.isBalanced}
          pending={session.summaryPending || prevLoading}
          isCreditCard={isCreditCard}
          currency={props.currency}
        />

        {session.summaryPending && !needsStartingBalance && (
          <p className="text-muted-foreground text-xs">
            Loading all transactions in this period…
          </p>
        )}

        {/* Transactions dated after the anchor are an explicit opt-in:
            rare, but the server accepts them (late-posting rows can belong to
            this statement even though the balance predates them). The button
            is always visible so the affordance is discoverable; once clicked
            it stays as a disabled status marker (loading → result). */}
        {!needsStartingBalance && (
          <LoadMoreRow>
            <LoadMoreButton
              type="button"
              disabled={prevLoading || includeLater}
              onClick={() => setIncludeLater(true)}
            >
              {!includeLater
                ? 'Load later transactions'
                : laterLoading || laterHasMore
                  ? 'Loading…'
                  : laterTxns.length === 0
                    ? `No transactions after ${props.anchorDate}`
                    : 'All later transactions loaded'}
            </LoadMoreButton>
          </LoadMoreRow>
        )}

        {/* Transaction list. Already-reconciled rows stay visible but disabled
            for context — they're accounted for in the previous balance. Rows
            dated outside the window (late-posting transactions can belong to
            this statement — the server only checks the sum) are selectable but
            never auto-selected. */}
        {needsStartingBalance ? null : loading && allTxns.length === 0 ? (
          <Empty>Loading transactions…</Empty>
        ) : allTxns.length === 0 ? (
          <Empty>No transactions found.</Empty>
        ) : (
          <TransactionList>
              {allTxns.map((txn) => {
                const isReconciled = txn.reconciliation != null;
                return (
                  <TransactionRow
                    key={txn._id}
                    className={isReconciled ? 'opacity-50' : ''}
                  >
                    <input
                      type="checkbox"
                      checked={isReconciled || selected.has(txn._id)}
                      disabled={isReconciled}
                      onChange={() => toggle(txn._id)}
                      className="shrink-0"
                    />
                    <span className="text-foreground min-w-0 flex-1 truncate text-sm">
                      {txn.description}
                    </span>
                    {isReconciled && (
                      <span className="text-muted-foreground shrink-0 text-xs">
                        reconciled
                      </span>
                    )}
                    <span className="text-foreground shrink-0 text-sm tabular-nums">
                      {fmt(txn.amount, txn.currency)}
                    </span>
                    <span
                      className={`text-muted-foreground w-20 shrink-0 text-right text-xs ${
                        isReconciled || inWindow(txn)
                          ? ''
                          : 'italic underline decoration-dotted'
                      }`}
                      title={
                        isReconciled || inWindow(txn)
                          ? undefined
                          : 'Outside this period'
                      }
                    >
                      {txn.date}
                    </span>
                  </TransactionRow>
                );
              })}
          </TransactionList>
        )}

        {/* Mirrors the later-transactions button above: always visible, and
            once the full history up to the anchor is loaded it becomes a
            disabled end-of-list marker rather than disappearing. */}
        {!needsStartingBalance && (
          <LoadMoreRow>
            <LoadMoreButton
              type="button"
              // Also disabled while the window itself is still auto-paging in,
              // so the button can't race the auto-load effect.
              disabled={loading || !windowCovered || !hasMore}
              onClick={loadNextPage}
            >
              {loading
                ? 'Loading…'
                : hasMore
                  ? 'Load earlier transactions'
                  : 'All earlier transactions loaded'}
            </LoadMoreButton>
          </LoadMoreRow>
        )}

        {session.error && <ErrorText>{session.error}</ErrorText>}
      </form>
    </Modal>
  );
};
